import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  useConfirm,
  ActionList,
  ActionMenu,
  IconButton,
  Text,
  TextInput as PrimerTextInput,
  Textarea,
} from "@primer/react";
import {
  PlusIcon,
  SyncIcon,
  TrashIcon,
  KebabHorizontalIcon,
  PencilIcon,
  CheckIcon,
  XIcon,
  PaperAirplaneIcon,
} from "@primer/octicons-react";
import { useCommonContext } from "../../contexts/common";
import { useCommonSelectedHook } from "../../hooks/useCommonSelectedHook";
import { NotifyHelper } from "../../helpers/toast";
import Button from "../../components/ui/button";
import MyButton from "../../components/ui/button/Button";
import { ketQuaThiDauVaoApi, CreateWithSubjectsRequest } from "../../api/ketQuaThiDauVaoApi";
import { KetQuaThiDauVaoItemResponse } from "../../models/response/quan-ly-thi/ex_ketquathidauvao";
import moment from "moment";
import { IHocSinh } from "../../models/response/crm-hocsinh/IHocsinh";
import ThiDauVaoMonThiTable, { ThiDauVaoMonThiTableRef } from "./ThiDauVaoMonThiTable";
import CreateLanThiModal from "./CreateLanThiModal";
import ComboboxTrangThaiThiSinh from "../../components/combobox-trangthai-thisinh/ComboboxTrangThaiThiSinh";
import SendMailTrungTuyenModal from "../ket-qua-thi-dau-vao/SendMailTrungTuyenModal";
import { dmMailTrungTuyenApi } from "../../api/dmMailTrungTuyenApi";
import { trangthai_gui_mail_trungtuyen } from "../../models/response/category/trangthai_gui_mail_trungtuyen";
import { Label } from "@primer/react";

interface ThiDauVaoProps {
  hocSinh: IHocSinh;
}

interface EditableLanThi {
  id: number;
  isEditing: boolean;
  ngay_thi: string;
  ghi_chu: string;
  ex_trangthai_thisinh_id: number;
}

const ThiDauVao: React.FC<ThiDauVaoProps> = ({ hocSinh }) => {
  const [ketQuaThiDauVaos, setKetQuaThiDauVaos] = useState<
    KetQuaThiDauVaoItemResponse[]
  >([]);

  const [editableLanThi, setEditableLanThi] = useState<{ [key: number]: EditableLanThi }>({});
  const [editingLanThiId, setEditingLanThiId] = useState<number | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isShowCreateModal, setIsShowCreateModal] = useState(false);
  const [isShowSendMailModal, setIsShowSendMailModal] = useState(false);
  const [emailHistory, setEmailHistory] = useState<trangthai_gui_mail_trungtuyen[]>([]);
  const tableRefs = useRef<{ [key: number]: ThiDauVaoMonThiTableRef | null }>({});
  const { translate } = useCommonContext();
  const { nam_hoc } = useCommonSelectedHook();
  const confirm = useConfirm();

  // Check xem đã gửi email trúng tuyển chưa
  const hasSentEmail = emailHistory.some(h => h.is_sended === true);

  const handleReloadAsync = useCallback(async () => {
    try {
      const res = await ketQuaThiDauVaoApi.selectByHocSinhNamHoc(
        hocSinh.id,
        nam_hoc
      );
      if (res.is_success) {
        console.log("ThiDauVao API Response:", res.data); // Debug log
        setKetQuaThiDauVaos(res.data || []);
        // Initialize editable state for each lần thi
        const editableState: { [key: number]: EditableLanThi } = {};
        (res.data || []).forEach((item: KetQuaThiDauVaoItemResponse) => {
          editableState[item.id] = {
            id: item.id,
            isEditing: false,
            ngay_thi: moment(item.ngay_thi).format("YYYY-MM-DD"),
            ghi_chu: item.ghi_chu || "",
            ex_trangthai_thisinh_id: item.ex_trangthai_thisinh_id || 0,
          };
        });
        setEditableLanThi(editableState);
      } else {
        NotifyHelper.Error(res.message ?? "");
      }
    } catch (error) {
      console.error(error);
      NotifyHelper.Error("Có lỗi xảy ra khi tải dữ liệu");
    }
  }, [hocSinh, nam_hoc]);

  useEffect(() => {
    if (hocSinh && hocSinh.id) {
      handleReloadAsync();
    }
  }, [hocSinh, handleReloadAsync]);

  // Load lịch sử gửi email
  const loadEmailHistory = useCallback(async () => {
    if (!hocSinh?.id) return;
    try {
      const res = await dmMailTrungTuyenApi.getHistoryByHocSinhId(hocSinh.id);
      if (res.is_success && res.data) {
        setEmailHistory(res.data);
      }
    } catch (error) {
      console.error("Error loading email history:", error);
    }
  }, [hocSinh?.id]);

  useEffect(() => {
    if (hocSinh?.id) {
      loadEmailHistory();
    }
  }, [hocSinh?.id, loadEmailHistory]);

  const handleDelete = async (id: number) => {
    if (
      await confirm({
        title: translate("Base.Label.Confirm"),
        content: translate("Base.Message.DeleteConfirm"),
        confirmButtonContent: "Xóa",
        cancelButtonContent: "Hủy",
        confirmButtonType: "danger",
      })
    ) {
      try {
        const res = await ketQuaThiDauVaoApi.delete(id);
        if (res.is_success) {
          NotifyHelper.Success(translate("Base.Message.DeleteSuccess"));
          handleReloadAsync();
        } else {
          NotifyHelper.Error(res.message ?? "");
        }
      } catch (error) {
        console.error(error);
        NotifyHelper.Error("Có lỗi xảy ra khi xóa dữ liệu");
      }
    }
  };

  const handleCreateNew = () => {
    setIsShowCreateModal(true);
  };

  const handleSubmitCreateLanThi = async (data: CreateWithSubjectsRequest) => {
    setIsCreating(true);
    try {
      const res = await ketQuaThiDauVaoApi.createWithSubjects(data);
      if (res.is_success) {
        NotifyHelper.Success(translate("Base.Message.CreateSuccess"));
        setIsShowCreateModal(false);
        handleReloadAsync();
      } else {
        NotifyHelper.Error(res.message ?? "");
      }
    } catch (error) {
      console.error(error);
      NotifyHelper.Error("Có lỗi xảy ra khi tạo lần thi mới");
    } finally {
      setIsCreating(false);
    }
  };

  const getNextLanThi = () => {
    return ketQuaThiDauVaos.length > 0
      ? Math.max(...ketQuaThiDauVaos.map(k => k.lan_thi)) + 1
      : 1;
  };



  const handleEditLanThi = (id: number) => {
    setEditingLanThiId(id);
    setEditableLanThi(prev => ({
      ...prev,
      [id]: { ...prev[id], isEditing: true }
    }));
  };

  const handleCancelEditLanThi = (id: number) => {
    const originalData = ketQuaThiDauVaos.find(item => item.id === id);
    if (originalData) {
      setEditableLanThi(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          isEditing: false,
          ngay_thi: moment(originalData.ngay_thi).format("YYYY-MM-DD"),
          ghi_chu: originalData.ghi_chu || "",
          ex_trangthai_thisinh_id: originalData.ex_trangthai_thisinh_id || 0,
        }
      }));
    }
    setEditingLanThiId(null);
    // Cancel all changes in table
    tableRefs.current[id]?.cancelAll();
  };

  const handleSaveLanThi = async (id: number) => {
    const editableData = editableLanThi[id];
    if (!editableData) return;

    try {
      // Save lần thi info first
      const originalData = ketQuaThiDauVaos.find(item => item.id === id);
      if (!originalData) return;

      const updateData = {
        ...originalData,
        ngay_thi: new Date(editableData.ngay_thi),
        ghi_chu: editableData.ghi_chu,
        ex_trangthai_thisinh_id: editableData.ex_trangthai_thisinh_id,
      };

      const res = await ketQuaThiDauVaoApi.update(updateData);
      if (res.is_success) {
        // Save all table data
        await tableRefs.current[id]?.saveAll();
        setEditableLanThi(prev => ({
          ...prev,
          [id]: { ...prev[id], isEditing: false }
        }));
        setEditingLanThiId(null);
        handleReloadAsync();
      } else {
        NotifyHelper.Error(res.message ?? "");
      }
    } catch (error) {
      console.error(error);
      NotifyHelper.Error("Có lỗi xảy ra khi lưu dữ liệu");
    }
  };

  const handleLanThiFieldChange = (id: number, field: keyof EditableLanThi, value: string | number) => {
    setEditableLanThi(prev => ({
      ...prev,
      [id]: { ...prev[id], [field]: field === "ex_trangthai_thisinh_id" ? Number(value) : value }
    }));
  };

  return (
    <Box>
      {/* Header compact */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Text sx={{ fontSize: 1, fontWeight: "semibold" }}>
            Kết quả thi đầu vào ({ketQuaThiDauVaos.length})
          </Text>
          {hasSentEmail && (
            <Label variant="success" size="small">
              Đã gửi email trúng tuyển
            </Label>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Button
            text="Gửi email trúng tuyển"
            leadingVisual={PaperAirplaneIcon}
            variant="default"
            size="small"
            disabled={ketQuaThiDauVaos.length === 0}
            onClick={() => setIsShowSendMailModal(true)}
          />
          <Button
            text="Thêm mới"
            leadingVisual={PlusIcon}
            variant="primary"
            size="small"
            onClick={handleCreateNew}
          />
          <MyButton
            text="Base.Label.Refresh"
            leadingVisual={SyncIcon}
            size="small"
            onClick={() => handleReloadAsync()}
          />
        </Box>
      </Box>

      {isShowSendMailModal && (
        <SendMailTrungTuyenModal
          isOpen={isShowSendMailModal}
          onClose={() => {
            setIsShowSendMailModal(false);
            // Reload lịch sử email sau khi đóng modal
            loadEmailHistory();
          }}
          selectedStudentIds={[hocSinh.id]}
          students={[hocSinh]}
        />
      )}

      {ketQuaThiDauVaos.map((ketQua) => (
        <Box key={ketQua.id} sx={{ mb: 2, border: "1px solid", borderColor: "border.default", borderRadius: 1 }}>
          {/* Header đơn giản của mỗi lần thi */}
          <Box
            sx={{
              px: 3,
              py: 2,
              borderBottom: "1px solid",
              borderColor: "border.default",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              bg: "canvas.subtle"
            }}
          >
            {/* Thông tin bên trái */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
              {/* Lần thi */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Text sx={{ fontSize: 0, fontWeight: 600 }}>Lần thi:</Text>
                <Text sx={{ fontSize: 1, fontWeight: 600 }}>{ketQua.lan_thi}</Text>
              </Box>

              <Text sx={{ color: "border.default" }}>|</Text>

              {/* Ngày thi */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Text sx={{ fontSize: 0, fontWeight: 600 }}>Ngày thi:</Text>
                <Text sx={{ fontSize: 1 }}>
                  {moment(ketQua.ngay_thi).format("DD/MM/YYYY")}
                </Text>
              </Box>

              {/* Trạng thái thí sinh */}
              {ketQua.ten_trangthai_thisinh && (
                <>
                  <Text sx={{ color: "border.default" }}>|</Text>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Text sx={{ fontSize: 0, fontWeight: 600 }}>Trạng thái:</Text>
                    <Label variant="accent" size="small">
                      {ketQua.ten_trangthai_thisinh}
                    </Label>
                  </Box>
                </>
              )}

              {/* Ghi chú - chỉ hiển thị khi có và không edit */}
              {!editableLanThi[ketQua.id]?.isEditing && ketQua.ghi_chu && (
                <>
                  <Text sx={{ color: "border.default" }}>|</Text>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
                    <Text sx={{ fontSize: 0, fontWeight: 600 }}>Ghi chú:</Text>
                    <Text sx={{
                      fontSize: 1,
                      maxWidth: "300px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap"
                    }}>
                      {ketQua.ghi_chu}
                    </Text>
                  </Box>
                </>
              )}
            </Box>

            {/* Nút thao tác bên phải */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {editableLanThi[ketQua.id]?.isEditing ? (
                <>
                  <IconButton
                    icon={CheckIcon}
                    variant="invisible"
                    size="small"
                    aria-label="Lưu"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSaveLanThi(ketQua.id);
                    }}
                    sx={{ color: "success.fg" }}
                  />
                  <IconButton
                    icon={XIcon}
                    variant="invisible"
                    size="small"
                    aria-label="Hủy"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCancelEditLanThi(ketQua.id);
                    }}
                    sx={{ color: "danger.fg" }}
                  />
                </>
              ) : (
                <>
                  <IconButton
                    icon={PencilIcon}
                    variant="invisible"
                    size="small"
                    aria-label="Chỉnh sửa"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditLanThi(ketQua.id);
                    }}
                  />
                  <ActionMenu>
                    <ActionMenu.Anchor>
                      <IconButton
                        icon={KebabHorizontalIcon}
                        variant="invisible"
                        size="small"
                        aria-label="Open menu"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </ActionMenu.Anchor>
                    <ActionMenu.Overlay>
                      <ActionList>
                        <ActionList.Item
                          onSelect={() => handleDelete(ketQua.id)}
                          variant="danger"
                        >
                          <ActionList.LeadingVisual>
                            <TrashIcon />
                          </ActionList.LeadingVisual>
                          {translate("Base.Label.Delete")}
                        </ActionList.Item>
                      </ActionList>
                    </ActionMenu.Overlay>
                  </ActionMenu>
                </>
              )}
            </Box>
          </Box>

          {/* Form chỉnh sửa bên trong container */}
          {editableLanThi[ketQua.id]?.isEditing && (
            <Box sx={{
              px: 2,
              py: 2,
              borderBottom: "1px solid",
              borderColor: "border.default",
              backgroundColor: "canvas.subtle"
            }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Text sx={{ fontWeight: "semibold", fontSize: 1, mb: 1 }}>
                  Chỉnh sửa thông tin lần thi
                </Text>

                <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                  {/* Ngày thi */}
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: "150px" }}>
                    <Text sx={{ fontSize: 0, fontWeight: "semibold" }}>Ngày thi:</Text>
                    <PrimerTextInput
                      type="date"
                      value={editableLanThi[ketQua.id]?.ngay_thi || ""}
                      onChange={(e) => handleLanThiFieldChange(ketQua.id, "ngay_thi", e.target.value)}
                      size="small"
                    />
                  </Box>

                  {/* Trạng thái */}
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: "200px" }}>
                    <Text sx={{ fontSize: 0, fontWeight: "semibold" }}>Trạng thái:</Text>
                    <ComboboxTrangThaiThiSinh
                      value={editableLanThi[ketQua.id]?.ex_trangthai_thisinh_id || 0}
                      onValueChanged={(id) => handleLanThiFieldChange(ketQua.id, "ex_trangthai_thisinh_id", id)}
                      isShowClearButton={true}
                    />
                  </Box>
                </Box>

                {/* Ghi chú */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Text sx={{ fontSize: 0, fontWeight: "semibold" }}>Ghi chú:</Text>
                  <Textarea
                    value={editableLanThi[ketQua.id]?.ghi_chu || ""}
                    onChange={(e) => handleLanThiFieldChange(ketQua.id, "ghi_chu", e.target.value)}
                    placeholder="Nhập ghi chú..."
                    rows={3}
                    resize="vertical"
                    sx={{ width: "100%" }}
                  />
                </Box>
              </Box>
            </Box>
          )}

          {/* Bảng môn thi chi tiết compact */}
          <Box sx={{ p: 2 }}>
            <ThiDauVaoMonThiTable
              ref={(ref) => { tableRefs.current[ketQua.id] = ref; }}
              ketQuaThiDauVaoId={ketQua.id}
              onDataChange={handleReloadAsync}
              isEditMode={editingLanThiId === ketQua.id}
            />
          </Box>
        </Box>
      ))}

      {ketQuaThiDauVaos.length === 0 && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Text sx={{ color: "fg.muted", fontSize: 1 }}>
            Chưa có kết quả thi đầu vào. Nhấn "Thêm mới" để bắt đầu.
          </Text>
        </Box>
      )}

      {/* Modal tạo lần thi mới */}
      <CreateLanThiModal
        isOpen={isShowCreateModal}
        onClose={() => setIsShowCreateModal(false)}
        onSubmit={handleSubmitCreateLanThi}
        hocSinhId={hocSinh.id}
        dmTruongId={hocSinh.dm_truong_id || 0}
        nextLanThi={getNextLanThi()}
        isLoading={isCreating}
      />
    </Box>
  );
};

export default ThiDauVao;
